/* eslint-disable react/prop-types */
// filepath: src/pages/ClientDashboard/NewClientDashboard/NewClientDashboardComponent.js
import React, { useEffect, useMemo, useState } from 'react';
import './NewClientDashboard.scss';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { HiOutlineLocationMarker } from 'react-icons/hi';
import { BsThreeDots } from 'react-icons/bs';
import { FiHelpCircle } from 'react-icons/fi';
import placeholder from '../../../assets/placeholder.jpg';
import base from '../../../apis';

import { toast } from 'react-toastify';
import { sampleSize } from 'lodash';
import { Button, Divider, Skeleton, Box } from '@mui/material';
import { FileDownloadOutlined, InfoOutlined, PeopleOutlineOutlined, StarRate, DarkModeOutlined, LightModeOutlined } from '@mui/icons-material';
import { useDarkMode } from '../../../hooks/useDarkMode';

const statusStyles = {
    required: { bg: '#fff1f0', color: '#b42318', border: '#ffd1ce', text: 'Action Required' },
    progress: { bg: '#EFF8FF', color: '#175CD3', border: '#d1d9ff', text: 'In Progress' },
    completed: { bg: '#ecfdf3', color: '#027a48', border: '#abefc6', text: 'Completed' },
};


// Loading component
const LoadingView = () => (
    <div className="client-dashboard">
        <div className="greeting-banner">
            <div className="container">
                <div className="greeting-content">
                    <div className="greeting-text">
                        <Skeleton variant="text" width={200} height={40} />
                        <Skeleton variant="text" width={350} height={20} />
                    </div>
                    <div className="header-actions">
                        <Skeleton variant="rectangular" width={200} height={40} sx={{ borderRadius: 1 }} />
                        <Skeleton variant="rectangular" width={150} height={40} sx={{ borderRadius: 1 }} />
                    </div>
                </div>
            </div>
        </div>

        <div className="container">
            <div className="stats-row">
                {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="card">
                        <Skeleton variant="text" width={120} height={20} />
                        <Skeleton variant="text" width={60} height={60} />
                    </div>
                ))}
            </div>

            <div className="main-grid">
                <div className="panel">
                    <div className="panel-header">
                        <Skeleton variant="text" width={120} height={24} />
                        <Skeleton variant="text" width={80} height={20} />
                    </div>
                    <div className="reports-list">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="report-item" style={{ border: '1px solid #e5e7eb' }}>
                                <Skeleton variant="text" width="100%" height={20} />
                                <Skeleton variant="text" width="70%" height={16} />
                                <Skeleton variant="rectangular" width="100%" height={1} sx={{ my: 2 }} />
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Skeleton variant="circular" width={32} height={32} />
                                        <Skeleton variant="text" width={100} height={16} />
                                    </Box>
                                    <Skeleton variant="rectangular" width={120} height={32} sx={{ borderRadius: 1 }} />
                                </Box>
                            </div>
                        ))}
                    </div>
                </div>

                <div style={{ display: 'grid', gap: 16 }}>
                    <div className="panel">
                        <Skeleton variant="text" width={120} height={24} />
                        <div className="quick-actions">
                            {[1, 2].map((i) => (
                                <div key={i} className="qa-item">
                                    <div className="left">
                                        <Skeleton variant="circular" width={18} height={18} />
                                        <Skeleton variant="text" width={100} height={16} />
                                    </div>
                                    <Skeleton variant="circular" width={16} height={16} />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="panel">
                        <Skeleton variant="text" width={120} height={24} />
                        <div className="agents">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="agent-card">
                                    <div className="left">
                                        <Skeleton variant="circular" width={40} height={40} />
                                        <div className="info">
                                            <Skeleton variant="text" width={120} height={16} />
                                            <Skeleton variant="text" width={150} height={14} />
                                        </div>
                                    </div>
                                    <Skeleton variant="circular" width={16} height={16} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

// Greeting banner
const GreetingBanner = React.memo(({ username, navigate, onToggleTheme, isDark }) => (
    <div className="greeting-banner">
        <div className="container">
            <div className="greeting-content">
                <div className="greeting-text">
                    <h1>Hi {username},</h1>
                    <p>Welcome back! Here's what's happening to your BPO Reports</p>
                </div>
                <div className="header-actions">
                    <button
                        className="btn-primary"
                        onClick={() => navigate('/order-bpo-report')}
                    >
                        + Request New BPO Report
                    </button>
                    <Button
                        startIcon={<PeopleOutlineOutlined />}
                        sx={{ color: '#7C380B' }}
                        variant='outlined'
                        onClick={() => navigate('/agent-finder')}
                    >
                        Browse Agents
                    </Button>
                    {/* Dark mode toggle for this dashboard only */}
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={onToggleTheme}
                        sx={{ ml: 1 }}
                        aria-label="Toggle dark mode"
                        aria-pressed={isDark}
                        startIcon={isDark ? <LightModeOutlined /> : <DarkModeOutlined />}
                    >
                        {isDark ? 'Light theme' : 'Dark theme'}
                    </Button>
                </div>
            </div>
        </div>
    </div>
));

const ActionRequiredBanner = ({ address, navigate, reportId }) => (
    <div className="container" role="status" aria-live="polite" aria-atomic="true">
        <div className="action-required">
            <div className="action-content">
                <InfoOutlined sx={{ color: '#dc2626', height: 20 }} />
                <div className="action-text">
                    <div className="label">Action Required</div>
                    <div className="desc">
                        Please fill in the details for <b>{address}</b> to proceed with your BPO report.
                    </div>
                </div>
            </div>
            <button
                className="cta"
                onClick={() => navigate(`/new-client-ordered-report?openModal=${reportId}&mode=edit`)}
            >
                Complete Details
            </button>
        </div>
    </div>
);

const StatCard = React.memo(({ color, label, value, bg }) => (
    <div className="card">

        <div style={{ background: bg, color }} className="kpi-label"><span className="dot" style={{ background: color }} />{label}</div>

        <div className="value">{value}</div>
    </div>
));

const ReportsPanel = React.memo(({ reports, navigate, loading, error, onRetry }) => (
    <div className="panel" role="region" aria-labelledby="reports-panel-header" aria-busy={loading} aria-live="polite">
        <div className="panel-header">
            <span id="reports-panel-header">Active Reports</span>
            <button
                className="btn-ghost"
                onClick={() => navigate('/new-client-ordered-report')}
                aria-label="View all reports"
            >
                View All
            </button>
        </div>
        <div className="reports-list" role="list">
            {loading ? (
                [1, 2, 3].map((i) => (
                    <div key={i} className="report-item" style={{ border: '1px solid #e5e7eb' }} role="listitem">
                        <Skeleton variant="text" width="100%" height={20} />
                        <Skeleton variant="text" width="70%" height={16} />
                        <Skeleton variant="rectangular" width="100%" height={1} sx={{ my: 2 }} />
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box display="flex" alignItems="center" gap={1}>
                                <Skeleton variant="circular" width={32} height={32} />
                                <Skeleton variant="text" width={100} height={16} />
                            </Box>
                            <Skeleton variant="rectangular" width={120} height={32} sx={{ borderRadius: 1 }} />
                        </Box>
                    </div>
                ))
            ) : error ? (
                <div className="error-state" role="alert">
                    <div className="title">Error loading reports</div>
                    <div className="desc">{error}</div>
                    <Button variant="contained" size="small" onClick={onRetry}>Retry</Button>
                </div>
            ) : reports.length === 0 ? (
                <div className="empty-state">
                    <div className="title">No active reports</div>
                    <div className="desc">Start by requesting your first BPO report.</div>
                    <Button variant="contained" size="small" onClick={() => navigate('/order-bpo-report')}>Request BPO Report</Button>
                </div>
            ) : (
                reports.map((r) => (
                    <ReportItem key={r.id} report={r} navigate={navigate} />
                ))
            )}
        </div>
    </div>
));

const ReportItem = React.memo(({ report, navigate }) => {
    const s = statusStyles[report.status];

    const handleAction = () => {
        if (report.status === 'completed') {
            navigate(`/preview-report/${report.id.replace('pr_', '')}`);
        } else if (report.status === 'required') {
            // Align with ActionRequiredBanner navigation
            navigate(`/new-client-ordered-report?openModal=${report.id.replace('pr_', '')}&mode=edit`);
        }
    };

    return (
        <div className="report-item" style={{ borderColor: s.border }} role="listitem">
            <div>
                <div className="top">
                    <div className="address"><HiOutlineLocationMarker color="#0ea5e9" />{report.address}</div>
                </div>
                <div className="meta">
                    <div className="badge" style={{ background: s.bg, color: s.color, }}>{s.text}</div>
                    <span>Ordered {report.ordered}</span>
                    <span>{report.sla}</span>
                </div>
            </div>
            <Divider sx={{ my: 3 }} />
            <div className='assignment'>
                <div className="assignee">
                    <span className="name">Assigned to:</span>
                    <img src={report.assignee.avatar} alt={report.assignee.name} loading="lazy" decoding="async" width="24" height="24" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = placeholder; }} />
                    <span className="name">{report.assignee.name}</span>
                </div>
                {/* don't show a button if the status is progress */}
                {report.status !== 'progress' && <div className="cta">
                    <Button
                        startIcon={report.status === 'required' ? <InfoOutlined /> : <FileDownloadOutlined />}
                        size='small'
                        className={`cta-btn${' outline'}`}
                        onClick={handleAction}
                        aria-label={report.status === 'completed' ? 'Download completed report' : 'Fill in property details'}
                    >
                        {report.status === 'completed' ? 'Download' : 'Fill in property details'}
                    </Button>
                </div>}
            </div>

        </div>
    );
});

const QuickActionsPanel = React.memo(({ navigate }) => (
    <div className="panel">
        <div className="panel-header">Quick Actions</div>
        <div className="quick-actions">
            <QuickAction
                navigate={navigate}
                route={'/new-client-ordered-report'}
                label="View All Reports"
            />
            <QuickAction
                navigate={navigate}
                route={'tel:1-888-616-5270'}
                label="Contact Support"
                isExternal={true}
            />
        </div>
    </div>
));

const QuickAction = React.memo(({ label, route, navigate, isExternal }) => {
    const handleClick = () => {
        if (isExternal) {
            window.location.href = route;
        } else {
            navigate(route);
        }
    };

    return (
        <div
            className="qa-item"
            onClick={handleClick}
            style={{ cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            aria-label={label}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); } }}
        >
            <div className="left">
                <div className="icon"><FiHelpCircle size={18} /></div>
                <div className="label">{label}</div>
            </div>
            <BsThreeDots color="#6b7280" />
        </div>
    );
});

const AgentsPanel = React.memo(({ agents, navigate, loading, error, onRetry }) => (
    <div className="panel" role="region" aria-labelledby="agents-panel-header" aria-busy={loading} aria-live="polite">
        <div className="panel-header">
            <span id="agents-panel-header">Your Agents</span>
            <Button
                className="btn-ghost"
                onClick={() => navigate('/agent-finder')}
                aria-label="View all agents"
            >
                View All
            </Button>
        </div>
        <div className="agents">
            {loading ? (
                [1, 2, 3].map((i) => (
                    <div key={i} className="agent-card">
                        <div className="left">
                            <Skeleton variant="circular" width={48} height={48} />
                            <div className="info">
                                <Skeleton variant="text" width={120} height={16} />
                                <Skeleton variant="text" width={150} height={14} />
                            </div>
                        </div>
                        <Skeleton variant="circular" width={16} height={16} />
                    </div>
                ))
            ) : error ? (
                <div className="error-state" role="alert">
                    <div className="title">Error loading agents</div>
                    <div className="desc">{error}</div>
                    <Button variant="contained" size="small" onClick={onRetry}>Retry</Button>
                </div>
            ) : agents.length === 0 ? (
                <div className="empty-state">
                    <div className="title">No agents yet</div>
                    <div className="desc">Find a top-rated local agent to help with your reports.</div>
                    <Button variant="contained" size="small" onClick={() => navigate('/agent-finder')}>Browse Agents</Button>
                </div>
            ) : (
                agents.map((a) => (
                    <AgentCard key={a.id} agent={a} navigate={navigate} />
                ))
            )}
        </div>
    </div>
));

const AgentCard = React.memo(({ agent, navigate }) => (
    <div
        className="agent-card"
        onClick={() => navigate(`/agent-finder/${agent.id}`)}
        style={{ cursor: 'pointer' }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/agent-finder/${agent.id}`); } }}
    >
        <div className="left">
            <div className="avatar"><img src={agent.avatar || placeholder} alt={agent.name} loading="lazy" decoding="async" width="48" height="48" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = placeholder; }} /></div>
            <div className="info">
                <div className="name">{agent.name}</div>
                <div className='agentRating'>
                    <span>
                        4.8<StarRate sx={{ color: '#FACC15', height: 20 }} />
                    </span>
                    <div style={{ height: '15px', width: '1px', backgroundColor: '#E9EAEB' }} />
                    <div className="meta">{agent.completed} reports completed</div>
                </div>
            </div>
        </div>
        <div className="right">
            <div className="action-icon">⋯</div>
        </div>
    </div>
));

export default function NewClientDashboardComponent() {
    const navigate = useNavigate();
    const username = useSelector((s) => s?.auth?.first_name)
    const client_register_id = useSelector((state) => state.auth.typeId);

    const [reports, setReports] = useState([]);
    const [agents, setAgents] = useState([]);
    const [pendingReport, setPendingReport] = useState(null);
    const [reportsLoading, setReportsLoading] = useState(true);
    const [agentsLoading, setAgentsLoading] = useState(true);
    const [reportsError, setReportsError] = useState(null);
    const [agentsError, setAgentsError] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);

    // Use centralized dark mode hook
    const { isDark, toggleTheme } = useDarkMode();

    const handleRetry = () => setRefreshKey((k) => k + 1);

    useEffect(() => {
        const ctrlPending = new AbortController();
        const ctrlReports = new AbortController();
        (async () => {
            setReportsLoading(true);
            setReportsError(null);
            try {
                // 1) Check for pending report that requires action
                const { data: pendingData } = await base.get('leads/check_pending_report/', { signal: ctrlPending.signal });
                if (Array.isArray(pendingData) && pendingData.length) {
                    const r = {
                        id: `pr_${pendingData[0].id}`,
                        address: pendingData[0]?.clients_physical_address,
                        status: 'required',
                        ordered: new Date().toLocaleDateString(),
                        sla: 'in status',
                        assignee: { name: '—', avatar: placeholder },
                    };
                    setPendingReport(r);
                    setReports([r]);
                } else {
                    setPendingReport(null);
                    setReports([]);
                }

                // 2) Load active/complete reports
                try {
                    const { data } = await base.get('/leads/search-report/', {
                        params: { client_register_id, lead_type: 'ordered' },
                        signal: ctrlReports.signal,
                    });
                    const items = Array.isArray(data) ? data : [];
                    const mapped = items.map((item) => ({
                        id: `pr_${item.id}`,
                        address: item.clients_physical_address,
                        sla: 'in status',
                        status: item.status?.toLowerCase() === 'complete' ? 'completed' : 'progress',
                        assignee: { name: `${item?.agent?.user?.first_name || ''} ${item?.agent?.user?.last_name || ''}`.trim() || '—', avatar: item?.agent?.profile_image || placeholder },
                        ordered: item.date_created ? new Date(item.date_created).toLocaleDateString() : '',
                    }));

                    setReports((prev) => {
                        const existingIds = new Set(prev.map((x) => x.id));
                        const merged = [...prev, ...mapped.filter((m) => !existingIds.has(m.id))];
                        return merged.slice(0, 3);
                    });
                } catch (err) {
                    if (err?.name === 'CanceledError' || err?.message === 'canceled') {
                        return;
                    }
                    setReportsError('Failed to load reports');
                }
            } catch (err) {
                if (err?.name === 'CanceledError' || err?.message === 'canceled') return;
                setReportsError('Failed to load reports');
            } finally {
                setReportsLoading(false);
            }
        })();
        return () => {
            ctrlPending.abort();
            ctrlReports.abort();
        };
    }, [client_register_id, refreshKey]);

    useEffect(() => {
        const ctrl = new AbortController();
        setAgentsLoading(true);
        setAgentsError(null);
        base
            .get('/register/agents/', { signal: ctrl.signal })
            .then(({ data }) => {
                const items = sampleSize(data?.results || [], 3).map((a, i) => ({
                    id: a.id || `a_${i}`,
                    name: `${a?.first_name || ''} ${a?.last_name || ''}`.trim() || 'Agent',
                    completed: a?.completed_reports || Math.floor(Math.random() * 200),
                    rating: a?.rating || 5,
                    avatar: a?.profile_image || placeholder,
                }));
                if (items.length) setAgents(items);
            })
            .catch((err) => {
                if (err?.name === 'CanceledError' || err?.message === 'canceled') return;
                setAgentsError('Failed to load agents');
                toast.dismiss();
            })
            .finally(() => {
                setAgentsLoading(false);
            });
        return () => ctrl.abort();
    }, [refreshKey]);

    const kpis = useMemo(() => {
        const req = reports.filter((r) => r.status === 'required').length;
        const prog = reports.filter((r) => r.status === 'progress').length;
        const done = reports.filter((r) => r.status === 'completed').length;
        return { req, prog, done, total: reports.length };
    }, [reports]);

    const firstRequiredAddress = useMemo(() => {
        return (pendingReport?.address) || (reports.find((r) => r.status === 'required')?.address);
    }, [pendingReport, reports]);

    const firstRequiredId = useMemo(() => {
        return (pendingReport?.id?.replace('pr_', '')) || (reports.find((r) => r.status === 'required')?.id?.replace('pr_', ''));
    }, [pendingReport, reports]);

    // Show loading view while data is being fetched
    if (reportsLoading && agentsLoading) {
        return <LoadingView />;
    }

    const displayName = username || 'there';

    return (
        <div className={`client-dashboard${isDark ? ' dark' : ''}`}>
            <GreetingBanner username={displayName} navigate={navigate} onToggleTheme={toggleTheme} isDark={isDark} />

            {firstRequiredAddress && (
                <ActionRequiredBanner
                    address={firstRequiredAddress}
                    navigate={navigate}
                    reportId={firstRequiredId}
                />
            )}

            <div className="container">
                <div className="stats-row">
                    <StatCard color="#ef4444" bg={'#ffd1ce'} label="Requires Action" value={kpis.req} />
                    <StatCard color="#3b82f6" bg='#EFF8FF' label="Active" value={kpis.prog} />
                    <StatCard color="#027A48" bg='#abefc6' label="Completed" value={kpis.done} />
                    <StatCard color="#7A5AF8" bg='#F4F3FF' label="Total" value={kpis.total} />
                </div>

                <div className="main-grid">
                    <div aria-busy={reportsLoading}>
                        <ReportsPanel reports={reports} navigate={navigate} loading={reportsLoading} error={reportsError} onRetry={handleRetry} />
                    </div>

                    <div style={{ display: 'grid', gap: 16 }} aria-busy={agentsLoading}>
                        <QuickActionsPanel navigate={navigate} />
                        <AgentsPanel agents={agents} navigate={navigate} loading={agentsLoading} error={agentsError} onRetry={handleRetry} />
                    </div>
                </div>
            </div>
        </div>
    );
}
